import { Intent, NonIdealState } from "@blueprintjs/core";
import { FormikProps } from "formik";
import {
  ButtonContainer,
  ButtonsBloc,
  FGCheckboxInput,
  FGCustomPanel,
  FGListen,
  FGTextAreaInput,
  FieldGroup,
  InlineButtonContainer,
  showError,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  DogOwnershipChangeRequestApi,
  DogOwnershipChangeRequestDto,
  DogTransientOwnersApi,
  EDogType,
  EPlatform,
  ERequestState,
  PersonApi,
  RequestApi,
  RequestDto,
  ResultOfBoolean,
  TransientOwnerSearch,
} from "../../../../api";
import {
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  PrintButton,
  SharepointButton,
  SmallFormGenerator,
  UploadSharepointButton,
} from "../../../../components";
import { useAuth, useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { RequestTransientPersonSelection } from "../../request/item/RequestTransientPersonSelection";

export interface IDogOwnershipChangeRequestDetailPageProps {
  dogOwnershipChangeRequestId: number;
  editMode: boolean;
}

export const DogOwnershipChangeRequestDetailPage: React.FunctionComponent<
  IDogOwnershipChangeRequestDetailPageProps
> = ({ dogOwnershipChangeRequestId, editMode }) => {
  const { user } = useAuth();
  const { t } = useTl();
  const transientApi = useApiService(DogTransientOwnersApi);
  const { currentLanguage } = useGlobalData();
  const history = useHistory();
  const api = useApiService(DogOwnershipChangeRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const { showDialogPromise, showDialog } = useDialog();
  const { openNewTab } = useWindow();
  const [ownerIdsBeforeEdition, setOwnerIdsBeforeEdition] = React.useState([]);

  const [reprintLoading, setreprintLoading] = React.useState(false);

  const saveCheck = React.useCallback(
    async (d: DogOwnershipChangeRequestDto) => {
      const cannotBeCreated = await api.isRSHCreationAllowed(d.dogId);
      if (cannotBeCreated.value) {
        showDialog({
          message: t(ETLCodes.RSHRequestCannotBeSavedTemporaryREquestConflict),
          title: t(ETLCodes.SaveRSHRequest),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        const isExport =
          dogOwnershipChangeRequestId <= 0
            ? await dogApi.isDogExport(d.dogId)
            : ResultOfBoolean.fromJS({ value: false });
        if (isExport.value) {
          const dr = await showDialogPromise({
            message: t(ETLCodes.DogIsExportedMessage),
            title: t(ETLCodes.Warning),
          });
          if (dr !== "yes") return;
        }
        //const result = await api.isDogOwner(d.dogId, d.request.personId, d.ownerIds);
        let isContactPersonRequiresWarning = false;

        const result = await dogApi.isDogOwnerOrBreeder(d.dogId, d.request.personId);
        if (!result.value && d?.request.status === ERequestState.REQUESTED) {
          isContactPersonRequiresWarning = true;
        }
        if (
          isContactPersonRequiresWarning &&
          (ownerIdsBeforeEdition?.includes(d.request.personId) || d.ownerIds?.includes(d.request.personId))
        ) {
          isContactPersonRequiresWarning = false;
        }
        if (isContactPersonRequiresWarning) {
          const dialogResult = await showDialogPromise({
            message: t(ETLCodes.IsNotDogOwnerOrBreederMessage),
            title: t(ETLCodes.Warning),
          });
          if (dialogResult !== "yes") return;
        }
        let isBanned = false;
        if (dogOwnershipChangeRequestId <= 0) {
          isBanned = (await personApi.isPersonExcludedQuery(d?.request?.personId)).value;
        }
        if (isBanned) {
          const isbannedValidation = await showDialogPromise({
            message: t(ETLCodes.PersonIsBannedConfirmMessage),
            title: t(ETLCodes.Warning),
          });
          if (isbannedValidation !== "yes") return;
        }
        const id = api.save(d);
        const updatedOwnerIds = [...ownerIdsBeforeEdition];
        d.ownerIds?.forEach((ownerId) => {
          if (!updatedOwnerIds.includes(ownerId)) {
            updatedOwnerIds.push(ownerId);
          }
        });
        setOwnerIdsBeforeEdition(updatedOwnerIds);
        return id;
      }
    },
    [api, dogApi, dogOwnershipChangeRequestId, ownerIdsBeforeEdition, personApi, showDialog, showDialogPromise, t]
  );

  const [dogIdValue, setDogIdValue] = React.useState<number>();
  const { data, loading, saving, saveItem, refresh } = useCrudApi<DogOwnershipChangeRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = dogOwnershipChangeRequestId) =>
          id > 0
            ? api.getDogOwnershipChangeRequest(id)
            : new DogOwnershipChangeRequestDto({
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: saveCheck,
        onSavedRoute: (d) => `${ERoutes.rsh}/${d.dogOwnershipChangeRequestId}/detail/edit`,
      }),
      [api, dogOwnershipChangeRequestId, saveCheck]
    ),
    [dogOwnershipChangeRequestId]
  );

  // const [ownerIdsValue, idsLoading, , refreshIds] = useApiEffect(async () => {
  //   if (dogIdValue) {
  //     return await transientApi.getIds(
  //       TransientOwnerSearch.fromJS({
  //         dogOwnershipChangeRequestId: dogOwnershipChangeRequestId,
  //         dogId: dogIdValue,
  //         languageId: currentLanguage.languageId,
  //       })
  //     );
  //   }
  // }, [transientApi, dogIdValue, currentLanguage]);

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .typeError(t(ETLCodes.Required))
        .required(t(ETLCodes.Required))
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, dogOwnershipChangeRequestId);
          return res.value;
        })
        .test("dog-type", t(ETLCodes.DogIsNotBelgianOrImport), async function (value) {
          const type = await dogApi.getDogType(value);
          return (
            type === EDogType.BelgianDogWithPedigree ||
            type === EDogType.ImportDog ||
            type === EDogType.DogWithoutPedigree
          );
        })
        .test("dog-hasOwner", t(ETLCodes.DogHasNoCurrentOwner), async function (value) {
          const res = await dogApi.hasOwner(value);
          return res.value;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (dogOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (dogOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("temporarily-ceaded", t(ETLCodes.OwnerHasTemporarilyCeaded), async function (value) {
            const res = await dogApi.isDogTemporarilyCeaded(dogIdValue, value);
            return !res.value;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (dogOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (dogOwnershipChangeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data, dogApi, dogIdValue, dogOwnershipChangeRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  const onDogIdChange = React.useCallback(
    async (value: number, formik: FormikProps<DogOwnershipChangeRequestDto>) => {
      const fromDb = await transientApi.getIds(
        TransientOwnerSearch.fromJS({
          dogOwnershipChangeRequestId: dogOwnershipChangeRequestId,
          dogId: value,
          languageId: currentLanguage.languageId,
        })
      );

      (window as any)._MYFORMIK = formik;
      setDogIdValue(value);
      setOwnerIdsBeforeEdition(fromDb);
      formik.setFieldValue("ownerIds", fromDb);
    },
    [currentLanguage.languageId, dogOwnershipChangeRequestId, transientApi]
  );

  const reprintRequest = React.useCallback(async () => {
    setreprintLoading(true);
    try {
      await api.reprintDocuments(dogOwnershipChangeRequestId);
    } catch {
      showError(t(ETLCodes.Error));
    }
    setreprintLoading(false);
  }, [api, dogOwnershipChangeRequestId, t]);

  const regenDocuments = React.useCallback(async () => {
    setreprintLoading(true);
    try {
      await api.regenExecutionDocuments(dogOwnershipChangeRequestId);
    } catch {
      showError(t(ETLCodes.Error));
    }
    setreprintLoading(false);
  }, [api, dogOwnershipChangeRequestId, t]);

  const onCancel = React.useCallback(() => history.push(`${ERoutes.rsh}`), [history]);
  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveItem}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        saving={saving}
        onCancel={onCancel}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="dogId" onChanged={onDogIdChange} />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: dogOwnershipChangeRequestId > 0 && (
              <ButtonContainer>
                <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
                {data?.request?.status === ERequestState.EXECUTED ? (
                  <>
                    <PrintButton text={t(ETLCodes.RePrint)} loading={reprintLoading} onClick={() => reprintRequest()} />
                    <UploadSharepointButton
                      text={t(ETLCodes.RegenDocuments)}
                      loading={reprintLoading}
                      onClick={() => regenDocuments()}
                    />
                  </>
                ) : null}
              </ButtonContainer>
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item: number) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              info={
                dogOwnershipChangeRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              requiredMark
              info={<InformationTooltip tooltipMessage={ETLCodes.MustBeOldOrNewOwnerOrAnyone} />}
              showWarning={true}
            />
            <FGSRSHCheckboxInput name="export" label={t(ETLCodes.Export)} disabled />
            <FGCheckboxInput label={t(ETLCodes.RegisteredLetter)} name="isRegisteredLetter" />
          </div>
          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
            rows={6}
          />
        </FieldGroup>
        {user.platform === EPlatform.PORTAL ? null : (
          <FGCustomPanel>
            {({ formik }) =>
              formik?.values?.dogId ? (
                <RequestTransientPersonSelection
                  linkedIds={formik?.values?.ownerIds || null}
                  onLinkedIdsChanged={async (ids) => {
                    formik?.setFieldValue("ownerIds", ids);
                    const res = await api.isRequestExport(ids);
                    formik.setFieldValue("export", res.value);
                  }}
                  editMode={editMode && !data?.request?.isBlocked && statusEditable}
                  loading={loading}
                />
              ) : (
                <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectADogToEditOwners)} />
              )
            }
          </FGCustomPanel>
        )}

        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.rsh}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
